// Typed contracts for the v2 listing read surface (HUBX-8472 / HUBX-8473, design page
// "Listing"). Kept beside items.component rather than in item-write/: these are READ shapes
// the listing owns, while item-write/item.models.ts stays the write boundary. The one shared
// member is ItemResponse, nested in each row precisely so it can go back through PUT verbatim.

import { ILookupCategory, ItemResponse } from '../item-write/item.models';

/** The seven EffectiveStatus buckets, spelled as the server's enum names. `GET /items`
 *  accepts them in `status`; absent means All. */
export type EffectiveStatusName =
  | 'Active'
  | 'OutOfStock'
  | 'Paused'
  | 'Pending'
  | 'Inactive'
  | 'Removed'
  | 'Archived';

/** A tab: one bucket, or All. */
export type ListingTab = EffectiveStatusName | 'All';

/**
 * One listing row (backend ListingRowResponse): the item resource, unchanged, plus read-only
 * decoration.
 *
 * `item` is exactly what `GET /items/{id}` returns. That is load-bearing: the multi-save sends
 * it straight back through `PUT /items/{id}` and undo restores from it, so it must stay
 * re-submittable verbatim. The members beside it are display-only and never travel back —
 * which is why the row is nested and not flat: flattening would mean stripping them before
 * every write, and that stripping is what silently forgets a member the next time the
 * envelope grows.
 */
export interface IListingRow {
  item: ItemResponse;
  priceDrop: boolean;
  justLaunched: boolean;
  /** ISO instant with offset (the columns store UTC). */
  lastActivatedOn: string | null;
  lastUpdatedOn: string;
  /** Operators only; null for a Seller. The key a VendorManager's grid looks up in its own
   *  assignment map. */
  companyId: string | null;
  companyName: string | null;
  /** Only for a Removed item with a stored cost inside a cost-reduction band. Truncated
   *  down to cents by the server, so typing it always reactivates (HUBX-8486). */
  reactivationThreshold: number | null;
  /** The ReductionPercent of the same band the threshold comes from (HUBX-8494): null
   *  exactly where the threshold is. Shown as is — deriving it from the truncated threshold
   *  would not give the configured value, and the bands never reach the client (HUBX-8476). */
  reactivationReductionPercent: number | null;
}

/** All four fields. `currentPage` echoes the page actually served, so asking past the end is
 *  answered with the page that really came back rather than the one requested. */
export interface IListingPagination {
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

/** `GET /items` body: rows and pagination only. Counters and filter options have their own
 *  routes (HUBX-8473, HUBX-8487). */
export interface IListingPage {
  items: IListingRow[];
  pagination: IListingPagination;
}

/**
 * The filter bar — the six controls the screen offers, and nothing else (design page
 * "Filters › The v2 filter set"). Tags and attributes are NOT listing filters: they are
 * dimensions of the legacy shared engine with no control on this screen. The raw status axes,
 * AvailabilityType, IsArchived, InStock and the price range are gone too.
 *
 * Shared by `/items` and `/items/status-counts`, which run the same filter block server-side
 * (HUBX-8485). `/items/filter-options` takes none of it (HUBX-8487).
 */
export interface IListingFilters {
  /** The visual group the client drew, already expanded: the Main row's id and its Subs.
   *  The server does a plain membership test and walks no tree. */
  categoryIds?: string[];
  manufacturerId?: string;
  promoId?: string;
  restrictions?: 'yes' | 'no';
  exw?: number;
  /** Search text. */
  q?: string;
}

/** A `GET /items` request: the filters plus what only the page itself reads. There is no
 *  `orderBy` (one sort: inventory) and no `pageSize` (25 is the contract). */
export interface IListingQuery extends IListingFilters {
  /** Absent is All. */
  status?: EffectiveStatusName;
  /** Absent is desc on the server, the screen's default. */
  sortDirection?: 'asc' | 'desc';
  /** 1-based. */
  page?: number;
}

/** `GET /items/filter-options` (HUBX-8487): the choices of every control on the filter bar,
 *  each already in display order, over the session vendor's whole catalogue — every row it
 *  fulfils, every status, archived included. No filter, tab, search or page moves a list.
 *  Empty arrays, never absent. The shapes follow what each dropdown binds: id/name,
 *  value/text for ExW. */
export interface IListingFilterOptions {
  manufacturers: { id: string; name: string | null }[];
  promos: { id: string; name: string | null }[];
  exw: { value: number; text: string | null }[];
  /** The category tree in exactly the shape `/lookups/categories` serves, cut down to what the
   *  vendor's catalogue reaches: a Main with Subs only through the Subs present, a Main
   *  without Subs by its own id. `children` is `[]` for a leaf, never null. Category binds the roots, Sub
   *  Category the chosen root's children. */
  categories: ILookupCategory[];
  /** The Restrictions values the vendor's catalogue holds — `yes` and/or `no` — i.e. the
   *  query-string values themselves, so the control sends back exactly what it was offered. */
  restrictions: string[];
}

/** `GET /items/status-counts` row as it travels (HUBX-8473): `status` is an ordinal —
 *  All = 0, then EffectiveStatus order — see STATUS_COUNT_CODES. */
export interface IStatusCountWire {
  status: number;
  count: number;
}

/** A counter as the tab strip consumes it. `code` is the wire ordinal, kept beside `tab` so the
 *  row can be matched back to what the server sent. */
export interface IStatusCounter {
  tab: ListingTab;
  code: number;
  count: number;
}

/**
 * The wire ordinal of each counter (HUBX-8473): All = 0, then the seven buckets in the order of
 * the backend's EffectiveStatus enum (Active … Archived), each shifted by one to make room for
 * All, which that enum does not have.
 *
 * NOT the legacy SkuStatus order, and therefore NOT interchangeable with
 * ListingSkuStatusValuesEnum: the two agree on All and Active and diverge from there (legacy
 * puts Inactive at 2 and appends Pending at 7). A code from this map must never be posted to a
 * legacy endpoint as a SkuStatus.
 */
export const STATUS_COUNT_CODES: Readonly<Record<number, ListingTab>> = Object.freeze({
  0: 'All',
  1: 'Active',
  2: 'OutOfStock',
  3: 'Paused',
  4: 'Pending',
  5: 'Inactive',
  6: 'Removed',
  7: 'Archived'
});
